import React from "react";
import { ACCOUNTING_LOGOS } from "../lib/site-data";
import { BookOpen, RefreshCcw, Receipt, CalendarDays, LineChart, Users2 } from "lucide-react";

const CORE = [
  {
    icon: BookOpen,
    title: "Daily Bookkeeping",
    body:
      "We handle transaction categorization and data entry in QuickBooks Online and Xero.",
  },
  {
    icon: RefreshCcw,
    title: "Reconciliations",
    body: "We perform bank and credit card reconciliations to ensure accuracy.",
  },
  {
    icon: Receipt,
    title: "AP & AR Management",
    body:
      "We manage invoice and bill posting with proper categorization, along with payment processing and receivable tracking.",
  },
  {
    icon: CalendarDays,
    title: "Month/Year-End Close",
    body:
      "We provide comprehensive support for timely and accurate month-end and year-end closing.",
  },
];

const APPROACH = [
  {
    icon: LineChart,
    title: "Management Reporting",
    body: "Financial summaries and custom reports to guide decision-making.",
  },
  {
    icon: Users2,
    title: "Our Role",
    body: "Confidential, process-driven, and documentation-focused.",
  },
  {
    icon: BookOpen,
    title: "Our Approach",
    body:
      "We function as an extended finance team for US-based businesses across multiple industries.",
  },
];

export const Bookkeeping = () => (
  <section
    id="bookkeeping"
    data-testid="bookkeeping-section"
    className="py-20 md:py-28 bg-[color:var(--brand-green-50)]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[color:var(--brand-green-200)] text-[color:var(--brand-green-700)] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase">
          Bookkeeping & Accounting
        </span>
        <h2
          data-testid="bookkeeping-title"
          className="font-display mt-5 text-4xl md:text-5xl font-bold text-navy"
        >
          Financial & Accounting Support for US Clients
        </h2>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          We provide end-to-end bookkeeping and accounting support focused on accuracy, timeliness,
          and clean financial records. We act as a seamless extension of your finance team. Our core
          platforms are <strong className="text-navy">QuickBooks Online (QBO)</strong> and{" "}
          <strong className="text-navy">Xero</strong>.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3 md:gap-4">
          {ACCOUNTING_LOGOS.map((l, i) => (
            <div
              key={l.name}
              data-testid={`accounting-logo-${i}`}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2"
            >
              <img
                src={l.src}
                alt={l.name}
                className="h-6 w-auto"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
              <span className="font-semibold text-slate-700 text-sm">{l.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <h3 className="font-display text-2xl text-navy">Our Core Bookkeeping Services</h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {CORE.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`bookkeeping-core-${i}`}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-green-100 text-[color:var(--brand-green-700)] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-display text-lg font-semibold text-navy">{c.title}</h4>
                </div>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-12">
        <h3 className="font-display text-2xl text-navy">Additional Expertise & Work Approach</h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {APPROACH.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                data-testid={`bookkeeping-approach-${i}`}
                className="bg-white rounded-2xl border border-slate-200 p-6"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-green-100 text-[color:var(--brand-green-700)] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-display text-lg font-semibold text-navy">{a.title}</h4>
                </div>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{a.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);
